import apiClient from '../config/axios';

export interface Branch {
  id: number;
  branch_name: string;
  address: string;
  phone: string;
  email: string;
  branch_type: string;
  tax_id?: string;
  license_number?: string;
  branch_managers?: number[];
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
  // Additional fields that might be available
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  website?: string;
  description?: string;
  established_date?: string;
  operating_hours?: string;
  contact_person?: string;
  alternate_phone?: string;
  alternate_email?: string;
  notes?: string;
}

export interface BranchRegistrationData {
  branch_name: string;
  address: string;
  phone: string;
  email: string;
  branch_type?: string;
  tax_id?: string;
  license_number?: string;
  is_active?: boolean;
}

export interface BranchResponse {
  status: string;
  message: string;
  data: {
    id: number;
    attributes: Branch;
  };
}

export interface BranchesListResponse {
  status: string;
  data: Array<{
    id: number;
    attributes: Branch;
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      pageCount: number;
    };
  };
}

class BranchService {
  /**
   * Register a new branch
   */
  async registerBranch(branchData: BranchRegistrationData): Promise<BranchResponse> {
    const response = await apiClient.post('/branches', branchData);
    return response.data;
  }

  /**
   * Get all branches with optional filtering
   */
  async getBranches(params?: {
    is_active?: boolean;
    search?: string;
    branch_type?: string;
    page?: number;
    pageSize?: number;
  }): Promise<BranchesListResponse> {
    const response = await apiClient.get('/branches', { params });
    return response.data;
  }

  /**
   * Get a specific branch by ID
   */
  async getBranchById(branchId: number): Promise<BranchResponse> {
    const response = await apiClient.get(`/branches/${branchId}`);
    return response.data;
  }

  /**
   * Update a branch
   */
  async updateBranch(branchId: number, updateData: Partial<Branch>): Promise<BranchResponse> {
    const response = await apiClient.put(`/branches/${branchId}`, updateData);
    return response.data;
  }

  /**
   * Delete a branch (hard delete)
   */
  async deleteBranch(branchId: number): Promise<BranchResponse> {
    const response = await apiClient.delete(`/branches/${branchId}`);
    return response.data;
  }

  /**
   * Activate a branch
   */
  async activateBranch(branchId: number): Promise<BranchResponse> {
    const response = await apiClient.patch(`/branches/${branchId}/activate`);
    return response.data;
  }

  /**
   * Deactivate a branch (soft delete)
   */
  async deactivateBranch(branchId: number): Promise<BranchResponse> {
    const response = await apiClient.patch(`/branches/${branchId}/deactivate`);
    return response.data;
  }

  /**
   * Get branches by type
   */
  async getBranchesByType(branchType: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchesListResponse> {
    const response = await apiClient.get(`/branches/type/${branchType}`, { params });
    return response.data;
  }

  /**
   * Search branches
   */
  async searchBranches(searchTerm: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchesListResponse> {
    const response = await apiClient.get(`/branches/search/${searchTerm}`, { params });
    return response.data;
  }
}

const branchService = new BranchService();
export default branchService;
